// src/components/AttackPathsDetail.js
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Server, Globe, Shield, AlertTriangle, Loader2, Info } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { fmtMoneyAtLeast as fmtMoney } from '../utils/moneyFormat';
import Navigation from './Navigation';
import Card from './Card';
import { chainStatus, chainVerdict } from '../utils/attackPathChain';
import { stageFigure, stageShares } from '../utils/attackPathStage';

const SEVERITY_BADGE = {
  CRITICAL: { bg: '#fed7d7', fg: '#c53030' },
  HIGH: { bg: '#feebc8', fg: '#c05621' },
  MODERATE: { bg: '#fefcbf', fg: '#975a16' },
  LOW: { bg: '#c6f6d5', fg: '#22543d' },
  INSUFFICIENT_DATA: { bg: '#e2e8f0', fg: '#4a5568' },
};

// Same cool-hue palette as the dashboard mini-card, keyed by path (paths with no open finding are
// left out, so an array index would shift) so a path's identity color is stable across both surfaces.
const PATH_STYLE = {
  'credential-cloud-exfil': { color: '#2a78d6', Icon: KeyRound },
  'edge-lateral-ransomware': { color: '#1baf7a', Icon: Server },
  'publicapp-cloud-databreach': { color: '#4a3aa7', Icon: Globe },
};
const pathStyle = key => PATH_STYLE[key] || { color: '#2a78d6', Icon: Shield };

const EFFORT_LABEL = { 1: 'Quick Win', 2: 'Standard', 3: 'Complex' };

function DetailSkeleton() {
  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-6">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          <span className="text-sm text-gray-500">Loading attack paths…</span>
        </div>
        <div className="h-8 w-72 bg-gray-200 rounded animate-pulse mb-6" />
        <div className="space-y-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-64 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}

function DetailEmpty() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8 text-center">
        <Shield className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">
          No attack paths have open findings.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-sm text-sky-600 hover:underline font-semibold"
        >
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
}

// One numbered path card: header (icon, numbered narrative label, severity badge) + a 3-column
// breakdown. Every figure is computed live from the org's own UnifiedRisk data; a stage with no
// findings reads "Risk forming" and carries no dollar figure.
function PathDetailCard({ path, index }) {
  const { Icon, color: pathColor } = pathStyle(path.pathKey);
  const color = path.severity === 'INSUFFICIENT_DATA' ? '#64748b' : pathColor;
  const badge = SEVERITY_BADGE[path.severity] || SEVERITY_BADGE.MODERATE;
  const stages = [...path.stages].sort((a, b) => a.stageOrder - b.stageOrder);
  const shares = stageShares(stages);
  const verdict = chainVerdict(chainStatus(path.stages));

  return (
    <Card className="!p-0 overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}1a` }}
          >
            <Icon className="w-4.5 h-4.5" style={{ color }} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-800 leading-snug">
              {index + 1}) {path.pathLabel}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {path.totalOpenRiskCount}/{path.totalRiskCount} open risks mapped · {fmtMoney(path.totalFinancialExposure)} live exposure
            </p>
          </div>
        </div>
        {path.hasSufficientData ? (
          <span
            className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded flex-shrink-0"
            style={{ backgroundColor: badge.bg, color: badge.fg }}
          >
            {path.severity}
          </span>
        ) : (
          <span className="text-[10px] text-gray-400 flex-shrink-0 px-2 py-1">Limited data</span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
        {/* Column 1 — the stage sequence itself, with real counts per stage. */}
        <div className="px-5 py-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2.5 flex items-center gap-1.5">
            <Server className="w-3 h-3" /> Attack Path Stages
          </p>
          <ol className="space-y-3">
            {stages.map(s => (
              <li key={s.stageName} className="flex gap-2.5">
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5"
                  style={(s.openRiskCount || 0) > 0
                    ? { backgroundColor: color, color: '#fff' }
                    : { border: '1.5px dashed #94a3b8', color: '#94a3b8', backgroundColor: '#fff' }}
                >
                  {s.stageOrder}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    {s.stageName}
                    {s.hasCriticalGap && (
                      <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" aria-label="Critical gap" />
                    )}
                  </div>
                  {(() => {
                    const f = stageFigure(s);
                    return (
                      <>
                        <div className={`text-xs ${f.kind === 'forming' ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>{f.status}</div>
                      </>
                    );
                  })()}
                  {s.signal && (
                    <div className="text-[11px] text-gray-500" title={s.signal.source}>
                      {s.signal.label}: <span className="font-semibold text-gray-700">{s.signal.value}</span>
                    </div>
                  )}
                  {(s.openRiskCount || 0) > 0 && s.worstRiskTitle && (
                    <div className="text-[11px] text-gray-400 truncate" title={s.worstRiskTitle}>
                      Worst: {s.worstRiskTitle}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {verdict && (
            <p className={`text-xs leading-snug mt-3 ${verdict.tone === 'complete' ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
              {verdict.text}
            </p>
          )}
        </div>

        {/* Column 2 — real per-stage dollar exposure (not the illustrative damage-category ranges
            the reference image used). */}
        <div className="px-5 py-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2.5 flex items-center gap-1.5">
            $ Financial Exposure (Live)
          </p>
          <div className="text-xl font-extrabold text-gray-800 mb-2.5">{fmtMoney(path.totalFinancialExposure)}</div>
          <ul className="space-y-1.5">
            {stages.map(s => {
              // Share of the summed stage amounts, not of the path total: a risk in two stages is in
              // both amounts but counted once in the total, so dividing by the total can pass 100%.
              const share = shares[s.stageName] || 0;
              return (
                <li key={s.stageName} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-gray-500 truncate">{s.stageName}</span>
                  <span className="text-gray-700 font-medium whitespace-nowrap">
                    {s.stageFinancialImpact > 0 ? fmtMoney(s.stageFinancialImpact) : '—'}
                    {share > 0 && (
                      <span
                        className="text-gray-400 font-normal"
                        title="Share of this path's stage figures. A risk can sit in more than one stage, so the stage figures can add up to more than the path total."
                      > ({share.toFixed(0)}%)</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Column 3 — real top fixes (already the org's own highest-impact-per-FTE-hour open risk
            per stage — see AttackPathAnalyzer.BuildTopFixes), not generic advice text. */}
        <div className="px-5 py-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2.5">
            Top Fixes (Live)
          </p>
          {path.topFixes?.length > 0 ? (
            <ul className="space-y-2.5">
              {path.topFixes.map(f => (
                <li key={f.riskId}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-gray-700 truncate" title={f.riskTitle}>
                      {f.riskTitle}
                    </span>
                    <span className="text-xs font-bold text-gray-800 whitespace-nowrap">
                      {fmtMoney(f.exposureReductionEstimate)}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {f.stageImpacted} · {EFFORT_LABEL[f.effortTier] || 'Standard'}
                    {f.requiresVendorCooperation && ' · Needs vendor'}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-400">No open, quantified risks to prioritize on this path.</p>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function AttackPathsDetail() {
  const apiCall = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchPaths = useCallback(() => {
    setLoaded(false);
    apiCall('GET', '/allRisks/attack-paths')
      .then(d => { setData(d ?? null); setLoaded(true); })
      .catch(() => { setData(null); setLoaded(true); });
  }, [apiCall]);

  useEffect(() => { fetchPaths(); }, [fetchPaths]);

  // There is no global scroll-to-top on route change, so arriving here from a scrolled dashboard
  // lands mid-page. Reset on mount, and again once the real content replaces the shorter skeleton
  // (the browser can otherwise keep a scroll offset the skeleton was too short to hold).
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (loaded) window.scrollTo(0, 0); }, [loaded]);

  if (!loaded) return <DetailSkeleton />;
  if (!data || !data.paths?.length) return <DetailEmpty />;

  const totalExposure = data.totalMappedExposure ?? 0;
  const orgExposure = data.orgCurrentExposure ?? 0;
  // A risk whose wording matches more than one path's narrative counts toward each of those paths'
  // own totals but only once in totalMappedExposure, so the per-path figures can legitimately sum
  // to more than the header total. pathSum − totalExposure is the total DUPLICATE-counted dollars
  // in that sum — a $1M risk on all 3 paths contributes $2M here, not $1M — so the copy must say
  // "duplicate exposure when added together", never "$X of exposure appears on multiple paths".
  // Surfaced as a dollar figure because a number is harder to skim past than a "not additive"
  // disclaimer. Threshold of $1 avoids a degenerate "$0" callout.
  const pathSum = data.paths.reduce((sum, p) => sum + (Number(p.totalFinancialExposure) || 0), 0);
  const duplicateExposure = Math.max(pathSum - totalExposure, 0);
  const pathCountWord = { 2: 'two', 3: 'three', 4: 'four' }[data.paths.length] || data.paths.length;
  const acrossLine = data.paths.length === 1
    ? 'Across the one attack path with open findings'
    : `Across all ${pathCountWord} ${duplicateExposure >= 1 ? 'overlapping ' : ''}attack paths`;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-800 mb-3"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
        </button>

        <h1 className="text-2xl font-bold text-gray-900">Top Attack Paths</h1>
        <p className="text-sm text-gray-500 mt-1">
          Attack path stages · Financial exposure · Highest-impact fixes — all computed live from
          this organization's own risk data, no industry-illustrative figures.
        </p>
        <p className="mt-3">
          <span className="text-xl font-bold text-gray-800">{fmtMoney(totalExposure)}</span>{' '}
          <span className="text-sm font-medium text-gray-600">unique mapped exposure</span>
          {orgExposure > 0 && (
            <span className="text-sm font-medium text-gray-600">
              {' '}of {fmtMoney(orgExposure)} org total
            </span>
          )}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {acrossLine} · each risk counted once
        </p>
        {duplicateExposure >= 1 && (
          <div className="flex items-start gap-2 text-xs text-sky-900 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2 mt-2 max-w-3xl leading-snug">
            <Info className="w-3.5 h-3.5 text-sky-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              Individual path totals are scenario views, not parts of a total — adding them
              together would include{' '}
              <span className="font-semibold">{fmtMoney(duplicateExposure)}</span> of duplicate
              exposure from risks shared across paths.
            </span>
          </div>
        )}
        {/* Methodology disclaimer: stages are keyword-matched against free-text risk data (no fixed
            attack-chain taxonomy exists yet — see AttackPathAnalyzer), and each "Top fix" is a single
            highest-$/effort-hour open risk per stage, not the stage's biggest risk or a combined sum. */}
        <p className="text-xs text-gray-400 mt-2 max-w-3xl leading-snug">
          Stages below are matched from each risk's category/title/description by keyword heuristic,
          not a fixed attack-chain taxonomy — a risk's wording can place it in an unexpected stage.
          "Risk forming" marks a stage with no findings yet — it carries no dollar figure. Each
          "Top Fix" is the single open risk with the highest dollar reduction per
          estimated remediation hour in that stage — not necessarily its largest risk, and not a sum
          across the listed fixes.
        </p>

        <div className="space-y-5 mt-6">
          {data.paths.map((p, i) => (
            <PathDetailCard key={p.pathKey} path={p} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
